import * as React from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { api, ApiError, qk } from '@client/lib/api';
import { useAdminInvestors, useAdminFirms, type AdminInvestor } from '@client/lib/admin-api';
import { PageHeader, InvestorStatusBadge } from '@client/components/admin/shared';
import { Button } from '@client/components/ui/button';
import { Dialog } from '@client/components/ui/overlay';
import { Field, Input, Select } from '@client/components/ui/input';
import { Spinner } from '@client/components/ui/feedback';
import { Table, Td, Th, Tr } from '@client/components/ui/table';
import { useToast } from '@client/components/ui/toast';
import { fmtRelative } from '@client/lib/format';

export const Route = createFileRoute('/admin/investors/')({ component: InvestorsPage });

function InvestorsPage() {
  const [q, setQ] = React.useState('');
  const investors = useAdminInvestors(q ? { q } : {});
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <PageHeader title="Investors" eyebrow="Firms → People" actions={<Button onClick={() => setOpen(true)}><Plus />Add person</Button>} />
      <div className="px-6 py-6 md:px-8">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email" className="mb-4 max-w-sm" />
        {investors.isPending ? <Spinner /> : (
          <div className="rounded-card border border-border bg-card">
            <Table>
              <thead><tr><Th>Person</Th><Th>Firm</Th><Th>Status</Th><Th align="right">Rooms</Th><Th align="right">Engagement 30d</Th><Th>Last seen</Th></tr></thead>
              <tbody>
                {investors.data?.investors.map((i) => (
                  <Tr key={i.id}>
                    <Td><Link to="/admin/investors/$id" params={{ id: i.id }} className="font-[520]">{i.name}</Link><span className="ml-2 text-muted">{i.email}</span>{i.title ? <span className="ml-2 text-muted">· {i.title}</span> : null}</Td>
                    <Td>{i.firm_name ?? <span className="text-muted">angel / none</span>}</Td>
                    <Td><InvestorStatusBadge status={i.status} /></Td>
                    <Td align="right">{i.active_grants}</Td>
                    <Td align="right"><span className="inline-block min-w-8 rounded-full bg-soft px-2 text-[12px] font-[570] text-brand">{i.engagement}</span></Td>
                    <Td className="text-muted">{i.lastSeenAt ? fmtRelative(i.lastSeenAt, 'en') : 'never'}</Td>
                  </Tr>
                ))}
                {investors.data?.investors.length === 0 ? <tr><Td colSpan={6} className="py-8 text-center text-muted">No investors yet.</Td></tr> : null}
              </tbody>
            </Table>
          </div>
        )}
      </div>
      {open ? <AddInvestorDialog onClose={() => setOpen(false)} /> : null}
    </>
  );
}

export function AddInvestorDialog({ onClose, firmId }: { onClose: () => void; firmId?: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const firms = useAdminFirms();
  const [form, setForm] = React.useState({ name: '', email: '', firm_id: firmId ?? '', title: '', locale: 'en' });
  const create = useMutation({
    mutationFn: () => api<{ investor: AdminInvestor }>('/api/admin/investors', { body: { ...form, firm_id: form.firm_id || null, title: form.title || null } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin'] }); toast.push({ tone: 'good', title: 'Person added' }); onClose(); },
    onError: (e) => toast.push({ tone: 'critical', title: 'Could not add', body: e instanceof ApiError ? (e.code === 'email_exists' ? 'This email already exists' : e.code) : String(e) }),
  });
  const valid = form.name.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title="Add person" description="They receive an invitation when a room is granted to them or to their firm." footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={() => create.mutate()} disabled={!valid} loading={create.isPending}>Add</Button></>}>
      <Field label="Name" htmlFor="inv-name"><Input id="inv-name" autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
      <Field label="Email" htmlFor="inv-email"><Input id="inv-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Firm" htmlFor="inv-firm"><Select id="inv-firm" value={form.firm_id} onChange={(e) => setForm({ ...form, firm_id: e.target.value })}><option value="">None (angel)</option>{firms.data?.firms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}</Select></Field>
        <Field label="Language" htmlFor="inv-locale"><Select id="inv-locale" value={form.locale} onChange={(e) => setForm({ ...form, locale: e.target.value })}><option value="en">English</option><option value="it">Italiano</option></Select></Field>
      </div>
      <Field label="Title (optional)" htmlFor="inv-title"><Input id="inv-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Partner" /></Field>
    </Dialog>
  );
}
